import { Temporal } from '@insio/dates';
import { EditorProps } from '../types/editor-props';
import { Datepicker } from '../../../../components/Datepicker';

/**
 * Simple date input editor for EditableField.
 * Renders an <Input> with the current value, and calls onChange/onBlur as needed.
 * Designed to be used as the default editor for date fields in the FieldEditor component,
 * but can also be used standalone when a basic date input is sufficient.
 */
function toPlainDate(value: unknown): Temporal.PlainDate | undefined {
  if (value instanceof Temporal.PlainDate) return value;
  if (typeof value !== 'string' || value === '') return undefined;
  try {
    return Temporal.PlainDate.from(value);
  } catch {
    return undefined;
  }
}

export function DateEditor({ id, value, onChange, onCommit, onBlur }: EditorProps) {
  const dateValue = toPlainDate(value);

  return (
    <Datepicker
      mode='single'
      id={id}
      defaultOpen
      value={dateValue}
      onChange={dt => {
        // Picking a date in the picker is itself a commit gesture — save
        // immediately so the toolbar isn't required (and so the click that
        // closes the picker doesn't fire the click-outside cancel).
        if (onCommit) {
          onCommit(dt);
        } else {
          onChange(dt);
        }
      }}
      onBlur={onBlur}
      autoFocus
      className='h-8 text-sm'
    />
  );
}
